import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import type { Email, Thread, ThreadMessageWithStatus } from '@/types/email';
import { fetchEmail, fetchEmails, fetchEmailsUnified, ApiError } from '@/api/emails';
import { fetchThread } from '@/api/threads';
import {
  buildReverseIndex,
  createEmailIndex,
  findIdentityById,
  mergeEmailUpdate,
  projectEmailsFromIndex,
  pruneEmailIndexByIds,
  upsertEmailIndex,
  type EmailIndex,
  type EmailReverseIndex,
  type MergeEmailOptions,
  type MergeUpdateOptions,
} from '@/lib/emailMerging';
import { mailEvents } from '@/events/mailEvents';
import { useAccountStore } from './accountStore';
import { useAttachmentStore } from './attachmentStore';
import { useFolderStore } from './folderStore';
import { useHoverStore } from './hoverStore';
import { useSenderGroupingStore } from './senderGroupingStore';
import {
  isRecentlyRemoved,
  isSenderBeingArchived,
  visibleEmailFilter,
  confirmServerFlags,
  applyOverlayToEmail,
  applyOverlayToIndex,
} from './emailStateHelpers';

const EMAIL_PAGE_SIZE = 50;
const PREFETCH_LIMIT = 20;
const MAX_EMAIL_CACHE_ENTRIES = 50;

type EmailCacheEntry = {
  index: EmailIndex;
  hasMore: boolean;
  lastAccessed: number;
};

/** Prune email cache to keep only the most recently accessed entries */
function pruneEmailCache(
  cache: Record<string, EmailCacheEntry>,
  maxSize: number = MAX_EMAIL_CACHE_ENTRIES,
): Record<string, EmailCacheEntry> {
  const keys = Object.keys(cache);
  if (keys.length <= maxSize) {
    return cache;
  }
  const sorted = keys.sort((a, b) => (cache[b].lastAccessed ?? 0) - (cache[a].lastAccessed ?? 0));
  const pruned: Record<string, EmailCacheEntry> = {};
  for (let i = 0; i < maxSize; i++) {
    pruned[sorted[i]] = cache[sorted[i]];
  }
  return pruned;
}

type EmailUpdate = Partial<Email> & { id: string };

type LoadOptions = {
  reset?: boolean;
  skipDedupe?: boolean;
};

/**
 * What the visible list currently shows. The sender-group and label-filter views
 * replace the list wholesale; modeling that as an explicit source (instead of
 * letting those stores write `emails` directly) keeps the email index the single
 * source of truth — every source projects through the same suppression-filtered
 * projection, and writers can validate they still own the list before applying
 * a late async result.
 */
export type ListSource =
  | { kind: 'mailbox' }
  | { kind: 'senderGroup'; groupKey: string }
  | { kind: 'labelFilter'; labelKey: string }
  | { kind: 'briefingItem'; itemKey: string };

type FolderSource = {
  accountId: string;
  folderId: string;
};

const makeSourceKey = (source: FolderSource) => `${source.accountId}:${source.folderId}`;

const dedupeSources = (sources: FolderSource[]): FolderSource[] => {
  if (!sources.length) {
    return [];
  }
  const seen = new Set<string>();
  return sources.filter((source) => {
    if (!source.accountId || !source.folderId) {
      return false;
    }
    const key = makeSourceKey(source);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const resolveFolderSources = (accountId: string, folderId: string): FolderSource[] => {
  if (!accountId || !folderId) {
    return [];
  }
  const folderState = useFolderStore.getState();
  if (accountId === UNIFIED_ACCOUNT_ID) {
    const unifiedFolder = folderState.folders.find((folder) => folder.id === folderId);
    return dedupeSources((unifiedFolder?.sourceFolders as FolderSource[]) ?? []);
  }

  const accountFolders = folderState.accountFolderCache[accountId] ?? [];
  const lookupCollections = accountFolders.length ? accountFolders : folderState.folders;
  const targetFolder =
    lookupCollections.find((folder) => folder.id === folderId) ??
    accountFolders.find((folder) => folder.id === folderId);

  if (targetFolder?.sourceFolders?.length) {
    return dedupeSources(targetFolder.sourceFolders as FolderSource[]);
  }

  return [{ accountId, folderId }];
};

// Stable identity for a unified source set (membership only — counts and
// ordering changes must not register as a difference).
const unifiedSourcesKey = (sources: FolderSource[]): string =>
  sources
    .map((source) => `${source.accountId}:${source.folderId}`)
    .sort()
    .join('|');

const clearLoadError = (
  errors: Record<string, string>,
  mailboxKey: string
): Record<string, string> => {
  if (!(mailboxKey in errors)) {
    return errors;
  }
  const next = { ...errors };
  delete next[mailboxKey];
  return next;
};

const sanitizeEmails = (emails: Email[]): Email[] =>
  emails.filter((email): email is Email => Boolean(email && email.id));

/**
 * The ONE projection used for the visible list. Read-time suppression
 * (recently-removed grace + sender-being-archived) is applied HERE, not only at
 * fetch time, so no write path — realtime hydration, digest close, direct
 * merges — can resurrect a suppressed email. Restoring after a failed action is
 * then simply "clear the marks and re-project".
 */
const projectEmailsClean = (index: EmailIndex, options?: MergeEmailOptions, previous?: Email[]): Email[] => {
  const projected = projectEmailsFromIndex(
    index,
    { visibilityFilter: visibleEmailFilter, ...options },
    previous
  );
  if (projected === previous) {
    return projected;
  }
  return sanitizeEmails(projected);
};

const applyAttachmentState = <T extends Email>(email: T): T => {
  if (!email.attachments?.length || !email.accountId) {
    return email;
  }

  const { downloadedByEmail } = useAttachmentStore.getState();
  const downloaded = downloadedByEmail[`${email.accountId}:${email.id}`];
  if (!downloaded?.size) {
    return email;
  }

  let changed = false;
  const attachments = email.attachments.map((attachment) => {
    if (attachment.downloaded || !downloaded.has(attachment.id)) {
      return attachment;
    }
    changed = true;
    return { ...attachment, downloaded: true };
  });

  return changed ? ({ ...email, attachments } as T) : email;
};

/**
 * P1/P14 (PERF_AUDIT): a one-message stand-in Thread built from the list row (or
 * the freshly-fetched message) so the thread SHELL renders from the known
 * messageCount the instant the email lands — the target message is visible while
 * its siblings stream in, with no single->thread remount flash (D2 preserved).
 */
const buildProvisionalThread = (
  email: Email,
  threadId: string,
  messageCount: number
): Thread => ({
  id: String(threadId),
  accountId: email.accountId,
  folderId: email.folderId ?? '',
  subject: email.subject ?? '(no subject)',
  snippet: email.snippet ?? undefined,
  messageCount,
  unreadCount: email.read ? 0 : 1,
  // Email.date is a Date (zod transform); Thread.lastMessageAt is an ISO string.
  lastMessageAt: email.date instanceof Date ? email.date.toISOString() : new Date().toISOString(),
  participants: [],
  messages: [email as ThreadMessageWithStatus],
});

/** Replace (or append) a message inside a thread by id, preserving order and the
 * declared messageCount — hydrates the target message's body in place before the
 * full thread response arrives. */
const mergeMessageIntoThread = (thread: Thread, email: Email): Thread => {
  const message = email as ThreadMessageWithStatus;
  const exists = thread.messages.some((m) => m.id === email.id);
  const messages = exists
    ? thread.messages.map((m) => (m.id === email.id ? message : m))
    : [...thread.messages, message];
  return { ...thread, messages };
};

/** Linear identity lookup for cache snapshots (the LIVE index uses the
 * maintained reverse index — findIdentityById(state.emailReverseIndex, …)). */
const findIdentityByEmailId = (index: EmailIndex, emailId: string): string | null => {
  for (const [identity, email] of Object.entries(index)) {
    if (email.id === emailId) {
      return identity;
    }
  }
  return null;
};

export const pruneEmailsFromCache = (
  cache: Record<string, EmailCacheEntry>,
  idSet: Set<string>
): Record<string, EmailCacheEntry> => {
  let changed = false;
  const nextCache: Record<string, EmailCacheEntry> = {};
  Object.entries(cache).forEach(([key, entry]) => {
    const nextIndex = pruneEmailIndexByIds(entry.index, idSet);
    if (nextIndex !== entry.index) {
      changed = true;
      nextCache[key] = { ...entry, index: nextIndex };
      return;
    }
    nextCache[key] = entry;
  });
  return changed ? nextCache : cache;
};

const patchEmailCache = (
  cache: Record<string, EmailCacheEntry>,
  update: EmailUpdate,
  mergeOptions: { allowEmptyArrayReplace?: boolean } = {}
): Record<string, EmailCacheEntry> => {
  let changed = false;
  const nextCache: Record<string, EmailCacheEntry> = {};
  Object.entries(cache).forEach(([key, entry]) => {
    // Linear scan by design: cache entries are bounded snapshots (≤50 entries
    // of ≤1 page each); a per-entry reverse index was declared but never
    // populated, so the "fast path" was dead weight.
    const identity = findIdentityByEmailId(entry.index, update.id);
    if (!identity) {
      nextCache[key] = entry;
      return;
    }
    const existingEmail = entry.index[identity];
    const updatedEmail = mergeEmailUpdate(existingEmail, update, mergeOptions);
    if (updatedEmail === existingEmail) {
      nextCache[key] = entry;
      return;
    }
    const nextIndex: EmailIndex = {
      ...entry.index,
      [identity]: updatedEmail,
    };
    changed = true;
    nextCache[key] = { ...entry, index: nextIndex };
  });
  return changed ? nextCache : cache;
};

export interface EmailStoreState {
  emails: Email[];
  emailsLoading: boolean;
  emailsInitialLoading: boolean;
  hasMoreEmails: boolean;

  selectedEmailId: string | null;
  selectedEmail: Email | null;
  selectedThreadId: string | null;
  selectedThread: Thread | null;
  /** The open message belongs to a thread whose fetch failed (retryable). */
  selectedThreadLoadFailed: boolean;
  emailDetailLoading: boolean;

  emailCache: Record<string, EmailCacheEntry>;
  emailLoadRequestId: number;
  emailSelectRequestId: number;
  emailLoadAbortController: AbortController | null;
  inFlightRequests: Map<string, { promise: Promise<void>; requestId: number }>;
  pendingUnifiedRequest: { accountId: string; folderId: string; options: LoadOptions } | null;
  // Source membership used by the most recent unified mailbox load. The folder
  // watch reloads when the resolved membership changes — heals a cold-start
  // load that ran while only some accounts' folder lists had arrived.
  lastUnifiedLoad: { accountId: string; folderId: string; sourcesKey: string } | null;
  emailIndex: EmailIndex;
  emailReverseIndex: EmailReverseIndex;
  listSource: ListSource;
  /** Last load failure per mailbox key (`accountId:folderId`) — drives the
   * list's error block + Retry instead of a misleading "No emails found". */
  emailLoadErrors: Record<string, string>;
  mailboxOffsets: Record<string, number>;
  pendingLoadMore: { accountId: string; folderId: string; force?: boolean } | null;

  loadEmails: (
    accountId: string,
    folderId: string,
    options?: LoadOptions
  ) => Promise<void>;
  loadMoreEmails: (accountId: string, folderId: string, options?: { force?: boolean }) => Promise<void>;
  selectEmail: (
    emailId: string,
    accountId: string,
    folderId: string,
    options?: {
      threadId?: string | null;
      /**
       * Suppress the built-in failure toast + selection clear (N23) — for
       * callers that orchestrate retries across candidate accounts (deep
       * links) and surface ONE failure themselves.
       */
      silentFailure?: boolean;
    }
  ) => Promise<boolean>;
  selectThread: (threadId: string, accountId: string) => Promise<boolean>;
  /**
   * Refresh the open thread's data IN PLACE: no remount (selectedThread is
   * replaced, never nulled), no selection hijack, no history entry. A data
   * update, not a navigation.
   */
  refreshSelectedThread: (threadId: string, accountId: string) => Promise<void>;
  appendOptimisticThreadMessage: (message: ThreadMessageWithStatus) => void;
  removeOptimisticThreadMessage: (messageId: string) => void;
  updateEmail: (email: EmailUpdate) => void;
  removeEmail: (emailId: string) => void;
  prefetchEmails: (accountId: string, folderId: string) => Promise<void>;
  /** P15 (PERF_AUDIT): warm a single email's body (and its thread, if multi-
   * message) into the cache on a strong open signal (row hover / keyboard focus)
   * so the actual open is a cheap 304 + optimistic header-with-body. */
  prefetchEmail: (
    emailId: string,
    accountId: string,
    folderId: string | null,
    threadId?: string | null,
    messageCount?: number
  ) => Promise<void>;
  clearEmails: () => void;
  invalidateCacheForAccount: (accountId: string) => void;
  clearSelection: () => void;
  updateEmailInPlace: (emailId: string, updates: Partial<Email>) => void;
  mergeEmails: (emails: Email[]) => void;
  /**
   * The single upsert applier (one writer): index write, reverse-index rebuild,
   * suppression-filtered re-projection, selectedEmail/selectedThread sync. Both
   * realtime paths and mergeEmails route through this.
   */
  applyEmailUpserts: (emails: Email[], options?: MergeUpdateOptions) => void;
  /**
   * Wholesale list replacement for non-mailbox sources (sender group, label
   * filter). Replaces the index, stamps the list source, applies the optimistic
   * flag overlay, and projects through the same suppression-filtered projection.
   */
  replaceList: (
    emails: Email[],
    opts: {
      source: ListSource;
      hasMore: boolean;
      loading?: boolean;
      initialLoading?: boolean;
    }
  ) => void;
  /** Recompute the visible projection (used after suppression marks change). */
  reprojectEmails: () => void;
  invalidateLoadRequests: () => void;
}

const scheduleMicrotask =
  typeof queueMicrotask === 'function'
    ? queueMicrotask
    : (callback: () => void) => {
        setTimeout(callback, 0);
      };

export const useEmailStore = create<EmailStoreState>()(
  subscribeWithSelector(
  conditionalDevtools(
    (set, get) => {
      const flushPendingLoadMore = (accountId: string, folderId: string) => {
        const pending = get().pendingLoadMore;
        if (!pending) {
          return;
        }
        if (pending.accountId !== accountId || pending.folderId !== folderId) {
          return;
        }
        set({ pendingLoadMore: null });
        if (import.meta.env.DEV) {
          console.debug('[EmailStore] loadMoreEmails:flushing pending request', {
            accountId,
            folderId,
            force: Boolean(pending.force),
          });
        }
        scheduleMicrotask(() => {
          void get().loadMoreEmails(accountId, folderId, pending.force ? { force: true } : {});
        });
      };

      return {
      emails: [],
      emailsLoading: false,
      emailsInitialLoading: false,
      hasMoreEmails: false,
      selectedEmailId: null,
      selectedEmail: null,
      selectedThreadId: null,
      selectedThread: null,
      selectedThreadLoadFailed: false,
      emailDetailLoading: false,
      emailCache: {},
      emailLoadRequestId: 0,
      emailSelectRequestId: 0,
      emailLoadAbortController: null,
      inFlightRequests: new Map(),
      pendingUnifiedRequest: null,
      lastUnifiedLoad: null,
      emailIndex: {},
      emailReverseIndex: new Map(),
      listSource: { kind: 'mailbox' },
      emailLoadErrors: {},
      mailboxOffsets: {},
      pendingLoadMore: null,

      loadEmails: async (accountId, folderId, options: LoadOptions = { reset: true }) => {
        const isReset = options.reset !== false;

        const requestKey = `${accountId}:${folderId}:${isReset ? 'reset' : 'append'}`;
        const allowConcurrent = Boolean(options.skipDedupe);
        const existing = get().inFlightRequests.get(requestKey);
        // Reuse an in-flight request ONLY when it is still the CURRENT load
        // generation. A superseded request (its apply is gated off by a newer
        // emailLoadRequestId) must never be handed to a newer caller — they'd
        // await a promise whose result will be silently dropped (A9).
        if (existing && !allowConcurrent && existing.requestId === get().emailLoadRequestId) {
          if (import.meta.env.DEV) {
            console.debug('[EmailStore] loadEmails:reuse in-flight request', {
              accountId,
              folderId,
              isReset,
            });
          }
          return existing.promise;
        }
        const requestId = get().emailLoadRequestId + 1;
        if (import.meta.env.DEV) {
          console.debug('[EmailStore] loadEmails:start', {
            accountId,
            folderId,
            isReset,
            requestId,
            skipDedupe: Boolean(options.skipDedupe),
          });
        }
        set({ emailLoadRequestId: requestId });

        const dedupeKey = allowConcurrent ? `${requestKey}:force:${Date.now()}` : requestKey;

        const promise = (async () => {
          const previousController = get().emailLoadAbortController;
          previousController?.abort();
          const controller = new AbortController();
          set({ emailLoadAbortController: controller });

          const cacheKey = `${accountId}:${folderId}`;
          const cached = get().emailCache[cacheKey];
          const mailboxOffsets = { ...get().mailboxOffsets };
          const mailboxKey = `${accountId}:${folderId}`;
          const fallbackOffset = get().emails.reduce((total, email) => {
            if (accountId === UNIFIED_ACCOUNT_ID) {
              return total + 1;
            }
            return email.accountId === accountId && email.folderId === folderId ? total + 1 : total;
          }, 0);
          const previousMailboxOffset = isReset ? 0 : mailboxOffsets[mailboxKey] ?? fallbackOffset;

          if (isReset) {
            const cachedEntry = cached ?? null;
            if (cachedEntry) {
              // Re-assert any un-flushed optimistic read/star over the cached
              // index too — the cache is a prior server snapshot and would
              // otherwise show the stale flag under the loading skeleton.
              const cachedIndex = applyOverlayToIndex({ ...cachedEntry.index });
              set((state) => ({
                emailLoadErrors: clearLoadError(state.emailLoadErrors, mailboxKey),
                emailsInitialLoading: true,
                emailsLoading: true,
                emails: projectEmailsClean(cachedIndex),
                emailIndex: cachedIndex,
                emailReverseIndex: buildReverseIndex(cachedIndex),
                listSource: { kind: 'mailbox' },
                hasMoreEmails: cachedEntry.hasMore,
                mailboxOffsets: {
                  ...mailboxOffsets,
                  [mailboxKey]: 0,
                },
              }));
            } else {
              // No cache for this folder: CLEAR the projection so the list
              // renders the loading skeleton instead of the PREVIOUS folder's
              // interactive rows under the new header (A2 — clicking one of
              // those opened the wrong folder's email). A failed fetch is no
              // longer a blank-inbox hazard: the per-mailbox error state
              // renders an explicit error block with Retry.
              set((state) => ({
                emailLoadErrors: clearLoadError(state.emailLoadErrors, mailboxKey),
                emails: [],
                emailIndex: {},
                emailReverseIndex: new Map(),
                listSource: { kind: 'mailbox' },
                emailsInitialLoading: true,
                emailsLoading: true,
                hasMoreEmails: false,
                mailboxOffsets: {
                  ...mailboxOffsets,
                  [mailboxKey]: 0,
                },
              }));
            }
          } else {
            set((state) => ({
              emailLoadErrors: clearLoadError(state.emailLoadErrors, mailboxKey),
              emailsLoading: true,
            }));
          }

          const finalizeAbortController = () => {
            set((state) => {
              if (state.emailLoadAbortController === controller) {
                return { emailLoadAbortController: null };
              }
              return {};
            });
          };

          try {
            const offset = isReset ? 0 : previousMailboxOffset;
            const runUnifiedLoad = async () => {
              const sources = resolveFolderSources(accountId, folderId);
              if (!sources.length) {
                set((state) =>
                  state.emailLoadRequestId === requestId
                    ? {
                        pendingUnifiedRequest: {
                          accountId,
                          folderId,
                          options,
                        },
                        emailsLoading: false,
                        emailsInitialLoading: true,
                      }
                    : {}
                );
                return;
              }

              set({
                pendingUnifiedRequest: null,
                lastUnifiedLoad: { accountId, folderId, sourcesKey: unifiedSourcesKey(sources) },
              });

              // Use server-side unified query for proper global date ordering
              const unifiedOffset = isReset ? 0 : previousMailboxOffset;
              const result = await fetchEmailsUnified(sources, {
                limit: EMAIL_PAGE_SIZE,
                offset: unifiedOffset,
                signal: controller.signal,
              });

              const aggregated = result
                .map((email) => applyAttachmentState(email))
                .filter((email) => {
                  if (isRecentlyRemoved(email.id, email.folderId)) {
                    return false;
                  }
                  if (isSenderBeingArchived(email.from?.email, email.date, email.id)) {
                    return false;
                  }
                  return true;
                })
                .map((email) => {
                  // The server payload is authoritative EXCEPT where an optimistic
                  // read/star hasn't flushed yet. Confirm-then-overlay: clear the
                  // overlay if the server already matches, else re-assert the
                  // un-flushed optimistic flag so this reset can't revert it.
                  confirmServerFlags(email.id, email);
                  return applyOverlayToEmail(email);
                });

              const hasMore = result.length >= EMAIL_PAGE_SIZE;

              let applied = false;
              set((state) => {
                if (state.emailLoadRequestId !== requestId) {
                  if (import.meta.env.DEV) {
                    console.debug('[EmailStore] loadEmails:skipped (request superseded)', {
                      accountId,
                      folderId,
                      requestId,
                      stateRequestId: state.emailLoadRequestId,
                    });
                  }
                  return {};
                }
                const baseIndex = isReset ? {} : state.emailIndex;
                // Authoritative list payload — honor server-cleared arrays
                // (e.g. all labels removed) instead of preserving stale ones.
                const nextIndex = upsertEmailIndex(baseIndex, aggregated, { allowEmptyArrayReplace: true });
                const projected = projectEmailsClean(nextIndex);
                const nextReverse = buildReverseIndex(nextIndex);
                applied = true;
                return {
                  emails: projected,
                  emailIndex: nextIndex,
                  emailReverseIndex: nextReverse,
                  listSource: { kind: 'mailbox' },
                  hasMoreEmails: hasMore,
                  emailsLoading: false,
                  emailsInitialLoading: false,
                  emailCache: pruneEmailCache({
                    ...state.emailCache,
                    [cacheKey]: { index: { ...nextIndex }, hasMore, lastAccessed: Date.now() },
                  }),
                  mailboxOffsets: {
                    ...state.mailboxOffsets,
                    [mailboxKey]: unifiedOffset + aggregated.length,
                  },
                };
              });
              if (!applied) {
                return;
              }
              if (import.meta.env.DEV) {
                console.debug('[EmailStore] loadEmails:complete (unified)', {
                  accountId,
                  folderId,
                  requestId,
                  fetched: aggregated.length,
                  hasMore,
                  offset: unifiedOffset,
                  sourceCount: sources.length,
                });
              }
            };

              if (accountId === UNIFIED_ACCOUNT_ID) {
                await runUnifiedLoad();
                if (get().emailLoadRequestId === requestId) {
                  flushPendingLoadMore(accountId, folderId);
                }
              } else {
                set({ pendingUnifiedRequest: null, lastUnifiedLoad: null });
                const data = await fetchEmails(accountId, folderId, {
                  limit: EMAIL_PAGE_SIZE,
                  offset,
                  signal: controller.signal,
                });
                const normalized = data
                  .map(applyAttachmentState)
                  .filter((email) => {
                    if (isRecentlyRemoved(email.id, email.folderId)) {
                      return false;
                    }
                    if (isSenderBeingArchived(email.from?.email, email.date, email.id)) {
                      return false;
                    }
                    return true;
                  })
                  .map((email) => {
                    // Confirm-then-overlay (see unified branch above): protect an
                    // un-flushed optimistic read/star from being clobbered by the
                    // server payload on this reset.
                    confirmServerFlags(email.id, email);
                    return applyOverlayToEmail(email);
                  });
                const hasMore = data.length >= EMAIL_PAGE_SIZE;

                let applied = false;
                set((state) => {
                  if (state.emailLoadRequestId !== requestId) {
                    if (import.meta.env.DEV) {
                      console.debug('[EmailStore] loadEmails:skipped (request superseded)', {
                        accountId,
                        folderId,
                      requestId,
                      stateRequestId: state.emailLoadRequestId,
                    });
                  }
                      return {};
                    }
                    const baseIndex = isReset ? {} : state.emailIndex;
                    // Authoritative list payload — honor server-cleared arrays
                    // (e.g. all labels removed) instead of preserving stale ones.
                    const nextIndex = upsertEmailIndex(baseIndex, normalized, { allowEmptyArrayReplace: true });
                    const projected = projectEmailsClean(nextIndex);
                    const cacheIndex = { ...nextIndex };
                    const nextReverse = buildReverseIndex(nextIndex);
                    applied = true;
                    return {
                      emails: projected,
                      emailIndex: nextIndex,
                      emailReverseIndex: nextReverse,
                      listSource: { kind: 'mailbox' },
                      hasMoreEmails: hasMore,
                  emailsLoading: false,
                  emailsInitialLoading: false,
                  emailCache: pruneEmailCache({
                    ...state.emailCache,
                    [cacheKey]: { index: cacheIndex, hasMore, lastAccessed: Date.now() },
                  }),
                  mailboxOffsets: {
                    ...state.mailboxOffsets,
                    [mailboxKey]: offset + data.length,
                      },
                    };
                  });
                if (!applied) {
                  return;
                }
                if (get().emailLoadRequestId === requestId) {
                  flushPendingLoadMore(accountId, folderId);
                }
                if (import.meta.env.DEV) {
                  console.debug('[EmailStore] loadEmails:complete', {
                  accountId,
                  folderId,
                  requestId,
                  fetched: normalized.length,
                  offset,
                  hasMore,
                });
              }
            }
          } catch (error: unknown) {
            if (error instanceof Error && error.name === 'AbortError') {
              set((state) => {
                if (state.emailLoadRequestId !== requestId) {
                  return {};
                }
                return {
                  emailsLoading: false,
                  emailsInitialLoading: false,
                };
              });
              return;
            }
            console.error('Failed to load emails:', error);
            const message =
              error instanceof Error && error.message
                ? error.message
                : 'Failed to load emails';
            set((state) => {
              if (state.emailLoadRequestId !== requestId) {
                return {};
              }
              const nextErrors = { ...state.emailLoadErrors, [mailboxKey]: message };
              if (cached) {
                const fallbackIndex = { ...cached.index };
                const fallbackEmails = projectEmailsClean(fallbackIndex);
                return {
                  emails: fallbackEmails,
                  emailIndex: fallbackIndex,
                  emailReverseIndex: buildReverseIndex(fallbackIndex),
                  hasMoreEmails: cached.hasMore,
                  emailsLoading: false,
                  emailsInitialLoading: false,
                  emailLoadErrors: nextErrors,
                };
              }
              return {
                emailsLoading: false,
                emailsInitialLoading: false,
                emailLoadErrors: nextErrors,
              };
            });
          } finally {
            finalizeAbortController();
          }
        })();

        set((state) => {
          const next = new Map(state.inFlightRequests);
          next.set(allowConcurrent ? dedupeKey : requestKey, { promise, requestId });
          return { inFlightRequests: next };
        });

        promise.finally(() => {
          set((state) => {
            const next = new Map(state.inFlightRequests);
            next.delete(allowConcurrent ? dedupeKey : requestKey);
            return { inFlightRequests: next };
          });
        });

        return promise;
      },

      loadMoreEmails: async (accountId, folderId, options = {}) => {
        const { emailsLoading, hasMoreEmails } = get();
        if (import.meta.env.DEV) {
          console.debug('[EmailStore] loadMoreEmails:requested', {
            accountId,
            folderId,
            force: Boolean(options.force),
            emailsLoading,
            hasMoreEmails,
          });
        }
        if (!options.force && !hasMoreEmails) {
          return;
        }
        if (emailsLoading) {
          set((state) => {
            const pending = state.pendingLoadMore;
            if (
              pending &&
              pending.accountId === accountId &&
              pending.folderId === folderId
            ) {
              if (import.meta.env.DEV) {
                console.debug('[EmailStore] loadMoreEmails:queued existing pending request', {
                  accountId,
                  folderId,
                  previousForce: Boolean(pending.force),
                  nextForce: pending.force || Boolean(options.force),
                });
              }
              return {
                pendingLoadMore: {
                  accountId,
                  folderId,
                  force: pending.force || Boolean(options.force),
                },
              };
            }
            if (import.meta.env.DEV) {
              console.debug('[EmailStore] loadMoreEmails:queued new pending request', {
                accountId,
                folderId,
                force: Boolean(options.force),
              });
            }
            return {
              pendingLoadMore: {
                accountId,
                folderId,
                force: Boolean(options.force),
              },
            };
          });
          return;
        }
        await get().loadEmails(accountId, folderId, {
          reset: false,
          skipDedupe: Boolean(options.force),
        });
        if (import.meta.env.DEV) {
          console.debug('[EmailStore] loadMoreEmails:dispatched', {
            accountId,
            folderId,
            force: Boolean(options.force),
          });
        }
      },

      selectEmail: async (emailId, accountId, folderId, options = {}) => {
        // Opening a detail makes any hover state stale — the open email is the
        // keyboard target now (the action-target resolver puts the open detail
        // ahead of hover, this keeps the visual state consistent too).
        useHoverStore.getState().clearHover();
        const requestId = get().emailSelectRequestId + 1;

        // P14 (PERF_AUDIT): seed the pane from the row we already hold so the
        // header (sender/subject/snippet) paints instantly instead of a blank
        // placeholder for a full RTT; the body region skeletons until the fetch
        // lands. P2: thread size is known from the row (messageCount), so a
        // single-message conversation (the common case) skips the thread fetch
        // entirely, and the view shell is chosen up front — a known multi-message
        // thread opens straight into a provisional thread shell, so there is no
        // single->thread remount flash (D2 preserved).
        const priorState = get();
        const cachedIdentity = findIdentityById(priorState.emailReverseIndex, emailId);
        const cachedEmail =
          cachedIdentity !== null ? priorState.emailIndex[cachedIdentity] ?? null : null;
        const knownThreadId = options.threadId ?? cachedEmail?.threadId ?? null;
        const cachedMessageCount = cachedEmail?.messageCount ?? 1;
        const expectMultiMessage = Boolean(knownThreadId) && cachedMessageCount > 1;
        const provisionalThread =
          expectMultiMessage && cachedEmail && knownThreadId
            ? buildProvisionalThread(cachedEmail, knownThreadId, cachedMessageCount)
            : null;

        set({
          emailSelectRequestId: requestId,
          selectedEmailId: emailId,
          selectedEmail: cachedEmail,
          selectedThreadId: expectMultiMessage ? knownThreadId : options.threadId ?? null,
          selectedThread: provisionalThread,
          selectedThreadLoadFailed: false,
          emailDetailLoading: true,
        });

        try {
          // Prefetch the thread in parallel ONLY when we actually expect >1
          // message (P2 — single-message opens never pay for a discarded thread
          // round trip). The endpoints resolve the owning account from the
          // globally-unique id, so the unified sentinel from deep links works too.
          const earlyThreadPromise =
            expectMultiMessage && knownThreadId
              ? fetchThread(accountId, knownThreadId).catch((error) => {
                  console.error('Failed to load thread:', error);
                  return null;
                })
              : null;

          const serverEmail = applyAttachmentState(await fetchEmail(accountId, emailId, folderId));
          const resolvedAccountId = serverEmail.accountId ?? accountId;
          confirmServerFlags(serverEmail.id, serverEmail);
          const email = applyOverlayToEmail(serverEmail);
          if (get().emailSelectRequestId !== requestId) return false;

          // The server is authoritative on thread size, so a stale row that said
          // "single" but is really a thread still resolves correctly (P2's lazy
          // fallback), and vice-versa.
          const threadId = email.threadId ?? knownThreadId;
          const serverMessageCount = email.messageCount ?? cachedMessageCount;
          const isThread = Boolean(threadId) && serverMessageCount > 1;

          // P1 (PERF_AUDIT): commit the email — and STOP the body skeleton — the
          // moment it lands; never block the open on the thread round trip. For a
          // single message the open is complete here. For a thread we keep/seed
          // the thread shell and hydrate the target message's body in place,
          // leaving emailDetailLoading true only until the siblings arrive.
          set((state) => {
            if (state.emailSelectRequestId !== requestId) return {};
            const identity = findIdentityById(state.emailReverseIndex, email.id);
            // Always update through the index to keep emails and emailIndex in sync
            const prevIndexEmail = identity !== null ? state.emailIndex[identity] : null;
            const nextIndex =
              identity !== null
                ? {
                    ...state.emailIndex,
                    [identity]: mergeEmailUpdate(state.emailIndex[identity], email),
                  }
                : state.emailIndex;
            const mergedIndexEmail = identity !== null ? nextIndex[identity] : null;
            // P10 (PERF_AUDIT): opening a detail merges the fetched BODY into the
            // index, which never changes list membership, order, or any field the
            // list renders/filters/groups on (id/folderId/from/date/read/starred/
            // subject/snippet/threadId/messageCount). Only re-project — an
            // O(n log n) rebuild that hands EmailList a fresh `emails` array and
            // re-runs its grouping/pending/label passes — when a list-relevant
            // field actually changed; otherwise keep the existing array reference.
            const affectsList =
              !prevIndexEmail ||
              !mergedIndexEmail ||
              prevIndexEmail.read !== mergedIndexEmail.read ||
              prevIndexEmail.starred !== mergedIndexEmail.starred ||
              prevIndexEmail.subject !== mergedIndexEmail.subject ||
              prevIndexEmail.snippet !== mergedIndexEmail.snippet ||
              // date must be compared by VALUE: the zod transform mints a fresh
              // Date instance on every fetch, so a reference compare is always
              // unequal and would make this whole guard dead (every open would
              // re-project, which is exactly what P10 exists to avoid).
              prevIndexEmail.date?.getTime() !== mergedIndexEmail.date?.getTime() ||
              prevIndexEmail.folderId !== mergedIndexEmail.folderId ||
              prevIndexEmail.threadId !== mergedIndexEmail.threadId ||
              prevIndexEmail.messageCount !== mergedIndexEmail.messageCount ||
              prevIndexEmail.from?.email !== mergedIndexEmail.from?.email ||
              prevIndexEmail.from?.name !== mergedIndexEmail.from?.name ||
              // Review finding 9: other row-rendered fields the detail response
              // can change relative to the list row. Keep these cheap (scalar /
              // length) so the common no-op open still skips re-projection.
              prevIndexEmail.contactApprovalState !== mergedIndexEmail.contactApprovalState ||
              (prevIndexEmail.autoLabels?.length ?? 0) !== (mergedIndexEmail.autoLabels?.length ?? 0) ||
              (prevIndexEmail.attachments?.length ?? 0) !== (mergedIndexEmail.attachments?.length ?? 0);
            const nextEmails =
              nextIndex !== state.emailIndex && affectsList
                ? projectEmailsClean(nextIndex, undefined, state.emails)
                : state.emails;
            const shellThread =
              isThread && threadId
                ? mergeMessageIntoThread(
                    state.selectedThread && state.selectedThread.id === String(threadId)
                      ? state.selectedThread
                      : buildProvisionalThread(email, threadId, serverMessageCount),
                    email
                  )
                : null;
            return {
              selectedEmail: email,
              emailIndex: nextIndex,
              emails: nextEmails,
              selectedThreadId: isThread && threadId ? String(threadId) : null,
              selectedThread: shellThread,
              emailDetailLoading: isThread,
            };
          });

          if (!isThread || !threadId) {
            return true;
          }

          // Hydrate the full conversation (reusing the parallel prefetch when it
          // matches), then replace the provisional shell in place — the target
          // message stays mounted (stable id key), siblings fill in.
          let threadResponse: Awaited<ReturnType<typeof fetchThread>> | null = null;
          let threadLoadFailed = false;
          if (earlyThreadPromise && knownThreadId === threadId) {
            threadResponse = await earlyThreadPromise;
            threadLoadFailed = threadResponse === null;
          } else {
            try {
              threadResponse = await fetchThread(resolvedAccountId, threadId);
            } catch (error) {
              console.error('Failed to load thread:', error);
              threadLoadFailed = true;
            }
          }
          if (get().emailSelectRequestId !== requestId) return false;

          const thread = threadResponse
            ? {
                ...threadResponse,
                messages: threadResponse.messages.map((message) => {
                  const serverMessage = applyAttachmentState(message);
                  confirmServerFlags(serverMessage.id, serverMessage);
                  return applyOverlayToEmail(serverMessage);
                }),
              }
            : null;

          set((state) => {
            if (state.emailSelectRequestId !== requestId) return {};
            return {
              // A failed thread load keeps the provisional shell so the target
              // message stays visible; the pane surfaces the retry affordance (the
              // message DOES belong to a conversation; don't silently degrade).
              selectedThread: thread ?? state.selectedThread,
              selectedThreadId: String(threadId),
              selectedThreadLoadFailed: threadLoadFailed,
              emailDetailLoading: false,
            };
          });
          return true;
        } catch (error) {
          if (get().emailSelectRequestId !== requestId) return false;
          console.error('Failed to load email:', error);
          // Fail loud at the SOURCE (N23/D10): every caller — j/k, undo,
          // auto-advance, palette — otherwise left a silent empty detail pane;
          // only handleEmailSelect carried its own toast.
          if (options.silentFailure) {
            // Caller (deep-link loops, auto-advance) surfaces ONE failure itself.
            // The header was seeded synchronously (P14), so we MUST clear it here
            // or the caller's `!selectedEmail` failure check (auto-advance S3
            // graceful fallback, emailStateHelpers) never fires and the reader is
            // left showing a seeded header over an empty body. Guarded by
            // requestId so a newer open that started mid-flight isn't stomped.
            set((state) =>
              state.emailSelectRequestId === requestId
                ? { selectedEmail: null, selectedThread: null, selectedThreadId: null }
                : {}
            );
            return false;
          }
          const { toast } = await import('sonner');
          const status = error instanceof ApiError ? error.status : null;
          if (status === 404 || status === 410) {
            // The tapped row is STALE — the message is gone server-side (moved
            // or deleted before this open resolved). Prune the ghost row, drop
            // to the list quietly, and reload the folder so the list self-heals.
            // Re-tapping the same ghost no longer re-bounces with a scary toast
            // for as long as the list stays stale (S1/R10).
            get().removeEmail(emailId);
            get().clearSelection();
            toast('That message is no longer here');
            const reloadAccountId = useAccountStore.getState().selectedAccountId ?? accountId;
            const reloadFolderId = useFolderStore.getState().selectedFolderId() ?? folderId;
            mailEvents.emit('mail:mailbox:reload', {
              accountId: reloadAccountId,
              folderId: reloadFolderId,
              options: { reset: true },
            });
          } else {
            // Network / transient: the row is probably fine — keep it, drop to
            // the list, and offer a Retry instead of silently failing.
            get().clearSelection();
            toast.error("Couldn't open message", {
              action: {
                label: 'Retry',
                onClick: () => {
                  void get().selectEmail(emailId, accountId, folderId, options);
                },
              },
            });
          }
          return false;
        } finally {
          if (get().emailSelectRequestId === requestId) {
            set({ emailDetailLoading: false });
          }
        }
      },

      appendOptimisticThreadMessage: (message) => {
        set((state) => {
          if (!state.selectedThread || state.selectedThread.id !== message.threadId) {
            return {};
          }
          // Avoid duplicates if the message is already in the thread (e.g., echo)
          if (state.selectedThread.messages.some((m) => m.id === message.id)) {
            return {};
          }
          const nextMessages = [...state.selectedThread.messages, message];
          return {
            selectedThread: {
              ...state.selectedThread,
              messages: nextMessages,
              messageCount: nextMessages.length,
            },
          };
        });
      },

      removeOptimisticThreadMessage: (messageId) => {
        set((state) => {
          if (!state.selectedThread) {
            return {};
          }
          if (!state.selectedThread.messages.some((m) => m.id === messageId)) {
            return {};
          }
          const nextMessages = state.selectedThread.messages.filter((m) => m.id !== messageId);
          return {
            selectedThread: {
              ...state.selectedThread,
              messages: nextMessages,
              messageCount: nextMessages.length,
            },
          };
        });
      },

      selectThread: async (threadId, accountId) => {
        useHoverStore.getState().clearHover();
        const requestId = get().emailSelectRequestId + 1;
        set({
          emailSelectRequestId: requestId,
          selectedThreadId: threadId,
          selectedThread: null,
          selectedThreadLoadFailed: false,
          emailDetailLoading: true,
        });

        try {
          const threadResponse = await fetchThread(accountId, threadId);
          if (get().emailSelectRequestId !== requestId) return false;
          const thread = {
            ...threadResponse,
            messages: threadResponse.messages.map((message) => {
              const serverMessage = applyAttachmentState(message);
              confirmServerFlags(serverMessage.id, serverMessage);
              return applyOverlayToEmail(serverMessage);
            }),
          };
          const fallbackEmail =
            thread.messages[thread.messages.length - 1] ??
            get().emails.find((email) => email.threadId === threadId) ??
            null;

          set({
            selectedThread: thread,
            selectedEmailId: fallbackEmail?.id ?? null,
            selectedEmail: fallbackEmail ?? null,
          });
          return true;
        } catch (error) {
          if (get().emailSelectRequestId !== requestId) return false;
          console.error('Failed to load thread:', error);
          return false;
        } finally {
          if (get().emailSelectRequestId === requestId) {
            set({ emailDetailLoading: false });
          }
        }
      },

      refreshSelectedThread: async (threadId, accountId) => {
        try {
          const threadResponse = await fetchThread(accountId, threadId);
          set((state) => {
            // The user navigated away mid-fetch — drop the response.
            if (state.selectedThreadId !== threadId || !state.selectedThread) {
              return {};
            }
            const messages = threadResponse.messages.map((message) => {
              const serverMessage = applyAttachmentState(message);
              confirmServerFlags(serverMessage.id, serverMessage);
              return applyOverlayToEmail(serverMessage) as ThreadMessageWithStatus;
            });
            // Keep the reader's place: selectedEmailId/selectedEmail are NOT
            // reset to the newest message; sync selectedEmail's data if its
            // message is still in the thread.
            const updates: Partial<EmailStoreState> = {
              selectedThread: { ...threadResponse, messages },
              // A successful refresh clears any prior load-failed state — this is
              // also the retry path for a provisional shell whose thread fetch
              // failed (PERF_AUDIT review finding 3).
              selectedThreadLoadFailed: false,
            };
            if (state.selectedEmail) {
              const refreshed = messages.find((m) => m.id === state.selectedEmail!.id);
              if (refreshed) {
                updates.selectedEmail = mergeEmailUpdate(state.selectedEmail, refreshed);
              }
            }
            return updates;
          });
        } catch (error) {
          console.error('Failed to refresh thread in place', error);
          // Keep the provisional shell but re-surface the retry affordance.
          set((state) =>
            state.selectedThreadId === threadId ? { selectedThreadLoadFailed: true } : {}
          );
        }
      },

      updateEmail: (email) => {
        // Use allowEmptyArrayReplace since this may be called for explicit user actions
        const mergeOptions = { allowEmptyArrayReplace: true };
        set((state) => {
          const identity = findIdentityById(state.emailReverseIndex, email.id);
          const nextIndex =
            identity !== null
              ? {
                  ...state.emailIndex,
                  [identity]: mergeEmailUpdate(state.emailIndex[identity], email, mergeOptions),
                }
              : state.emailIndex;
          // Only re-project from index to keep emails and emailIndex in sync
          const nextEmails =
            nextIndex !== state.emailIndex
              ? projectEmailsClean(nextIndex, undefined, state.emails)
              : state.emails;
          const nextSelectedEmail =
            state.selectedEmail && state.selectedEmail.id === email.id
              ? mergeEmailUpdate(state.selectedEmail, email, mergeOptions)
              : state.selectedEmail;
          const shouldUpdateThread =
            !!state.selectedThread &&
            state.selectedThread.messages.some((message) => message.id === email.id);
          return {
            emails: nextEmails,
            emailIndex: nextIndex,
            selectedEmail: nextSelectedEmail,
            selectedThread: shouldUpdateThread
              ? {
                  ...state.selectedThread!,
                  messages: state.selectedThread!.messages.map((message) =>
                    message.id === email.id
                      ? mergeEmailUpdate(message, email, mergeOptions) as ThreadMessageWithStatus
                      : message
                  ),
                }
              : state.selectedThread,
            emailCache: patchEmailCache(state.emailCache, email, mergeOptions),
          };
        });
      },

      updateEmailInPlace: (emailId, updates) => {
        if (!emailId) {
          return;
        }
        const payload: EmailUpdate = { id: emailId, ...updates };
        // Use allowEmptyArrayReplace since this is for explicit user actions
        const mergeOptions = { allowEmptyArrayReplace: true };
        set((state) => {
          const identity = findIdentityById(state.emailReverseIndex, emailId);
          const nextIndex =
            identity !== null
              ? {
                  ...state.emailIndex,
                  [identity]: mergeEmailUpdate(state.emailIndex[identity], updates, mergeOptions),
                }
              : state.emailIndex;
          const nextEmails =
            nextIndex === state.emailIndex
              ? state.emails
              : projectEmailsClean(nextIndex, undefined, state.emails);
          const shouldUpdateThread =
            !!state.selectedThread &&
            state.selectedThread.messages.some((message) => message.id === emailId);
          return {
            emails: nextEmails,
            emailIndex: nextIndex,
            selectedEmail:
              state.selectedEmail && state.selectedEmail.id === emailId
                ? mergeEmailUpdate(state.selectedEmail, updates, mergeOptions)
                : state.selectedEmail,
            selectedThread: shouldUpdateThread
              ? {
                  ...state.selectedThread!,
                  messages: state.selectedThread!.messages.map((message) =>
                    message.id === emailId ? mergeEmailUpdate(message, updates, mergeOptions) as ThreadMessageWithStatus : message
                  ),
                }
              : state.selectedThread,
            emailCache: patchEmailCache(state.emailCache, payload, mergeOptions),
          };
        });
      },

      removeEmail: (emailId) => {
        set((state) => {
          const isSelected = state.selectedEmailId === emailId;
          const shouldClearThread =
            state.selectedThread?.messages.some((message) => message.id === emailId) &&
            (state.selectedThread?.messages.length ?? 0) <= 1;
          const idSet = new Set([emailId]);
          const nextIndex = pruneEmailIndexByIds(state.emailIndex, idSet);
          const nextEmails =
            nextIndex === state.emailIndex
              ? state.emails
              : projectEmailsClean(nextIndex, undefined, state.emails);
          return {
            emails: nextEmails,
            emailIndex: nextIndex,
            emailReverseIndex:
              nextIndex === state.emailIndex
                ? state.emailReverseIndex
                : buildReverseIndex(nextIndex),
            selectedEmailId: isSelected ? null : state.selectedEmailId,
            selectedEmail: isSelected ? null : state.selectedEmail,
            selectedThreadId: shouldClearThread ? null : state.selectedThreadId,
            selectedThread: shouldClearThread ? null : state.selectedThread,
            emailCache: pruneEmailsFromCache(state.emailCache, idSet),
          };
        });
      },

      clearEmails: () => {
        set({
          emails: [],
          emailIndex: {},
          emailReverseIndex: new Map(),
          listSource: { kind: 'mailbox' },
          hasMoreEmails: false,
          selectedEmailId: null,
          selectedEmail: null,
          selectedThreadId: null,
          selectedThread: null,
          mailboxOffsets: {},
          pendingLoadMore: null,
          pendingUnifiedRequest: null,
          lastUnifiedLoad: null,
        });
      },

      invalidateLoadRequests: () => {
        const controller = get().emailLoadAbortController;
        controller?.abort();
        set((state) => ({
          emailLoadRequestId: state.emailLoadRequestId + 1,
          emailLoadAbortController: null,
          pendingLoadMore: null,
        }));
      },

      clearSelection: () => {
        set((state) => ({
          selectedEmailId: null,
          selectedEmail: null,
          selectedThreadId: null,
          selectedThread: null,
          selectedThreadLoadFailed: false,
          emailDetailLoading: false,
          // Invalidate any in-flight selectEmail request so stale fetches
          // (e.g. a pending thread fetch) can't update state after navigation
          emailSelectRequestId: state.emailSelectRequestId + 1,
        }));
      },

      mergeEmails: (emailsToMerge) => {
        get().applyEmailUpserts(emailsToMerge ?? []);
      },

      applyEmailUpserts: (emailsToUpsert, mergeOptions) => {
        if (!emailsToUpsert?.length) {
          return;
        }
        const updateMap = new Map(emailsToUpsert.map((email) => [email.id, email]));
        let threadRefreshTarget: { threadId: string; accountId: string } | null = null;
        set((state) => {
          const nextIndex = upsertEmailIndex(state.emailIndex, emailsToUpsert, mergeOptions);
          const updates: Partial<EmailStoreState> = {};
          if (nextIndex !== state.emailIndex) {
            updates.emailIndex = nextIndex;
            updates.emailReverseIndex = buildReverseIndex(nextIndex);
            updates.emails = projectEmailsClean(nextIndex, undefined, state.emails);
          }

          if (state.selectedEmail && updateMap.has(state.selectedEmail.id)) {
            const merged = mergeEmailUpdate(
              state.selectedEmail,
              updateMap.get(state.selectedEmail.id)!,
              mergeOptions
            );
            if (merged !== state.selectedEmail) {
              updates.selectedEmail = merged;
            }
          }

          if (state.selectedThread) {
            const messages = state.selectedThread.messages.map((message) => {
              const incoming = updateMap.get(message.id);
              return incoming
                ? (mergeEmailUpdate(message, incoming, mergeOptions) as ThreadMessageWithStatus)
                : message;
            });
            const threadChanged = messages.some(
              (message, index) => message !== state.selectedThread!.messages[index]
            );
            if (threadChanged) {
              updates.selectedThread = {
                ...state.selectedThread,
                messages,
                unreadCount: messages.filter((message) => !message.read).length,
              };
            }

            // A new message for the open thread isn't in its message list yet —
            // request an (in-place) thread data refresh.
            const existingIds = new Set(state.selectedThread.messages.map((m) => m.id));
            const hasNewThreadMessage = emailsToUpsert.some(
              (email) => email.threadId === state.selectedThread!.id && !existingIds.has(email.id)
            );
            if (hasNewThreadMessage) {
              threadRefreshTarget = {
                threadId: state.selectedThread.id,
                accountId: state.selectedThread.accountId,
              };
            }
          }

          return Object.keys(updates).length ? updates : {};
        });

        if (threadRefreshTarget) {
          const { threadId, accountId } = threadRefreshTarget;
          setTimeout(() => {
            mailEvents.emit('mail:thread:refresh', { threadId, accountId });
          }, 100);
        }
      },

      replaceList: (emailsForList, opts) => {
        const nextIndex = applyOverlayToIndex(createEmailIndex(emailsForList));
        set({
          emails: projectEmailsClean(nextIndex),
          emailIndex: nextIndex,
          emailReverseIndex: buildReverseIndex(nextIndex),
          listSource: opts.source,
          hasMoreEmails: opts.hasMore,
          // A successful non-mailbox list (sender group / label filter) means the
          // visible list is healthy — drop any stale per-folder load-error so its
          // "Couldn't load this folder" banner (and its Retry, which would clobber
          // the group/label list) doesn't render over it (L8).
          emailLoadErrors: {},
          ...(opts.loading !== undefined ? { emailsLoading: opts.loading } : {}),
          ...(opts.initialLoading !== undefined ? { emailsInitialLoading: opts.initialLoading } : {}),
        });
      },

      reprojectEmails: () => {
        set((state) => {
          const next = projectEmailsClean(state.emailIndex, undefined, state.emails);
          return next === state.emails ? {} : { emails: next };
        });
      },

      invalidateCacheForAccount: (accountId) => {
        set((state) => {
          const nextCache: Record<string, EmailCacheEntry> = {};
          Object.entries(state.emailCache).forEach(([key, value]) => {
            if (!key.startsWith(`${accountId}:`)) {
              nextCache[key] = value;
            }
          });
          return { emailCache: nextCache };
        });
      },

      prefetchEmails: async (accountId, folderId) => {
        if (!accountId || !folderId || accountId === UNIFIED_ACCOUNT_ID) {
          return;
        }
        const cacheKey = `${accountId}:${folderId}`;
        const cached = get().emailCache[cacheKey];
        if (cached && Object.keys(cached.index).length) {
          return;
        }

        const requestKey = `prefetch:${cacheKey}`;
        const existing = get().inFlightRequests.get(requestKey);
        if (existing) {
          return existing.promise;
        }

        const promise = (async () => {
          try {
            const data = await fetchEmails(accountId, folderId, {
              limit: PREFETCH_LIMIT,
              offset: 0,
            });
            const normalized = data.map(applyAttachmentState);
            const index = createEmailIndex(normalized);
            set((state) => ({
              emailCache: pruneEmailCache({
                ...state.emailCache,
                [cacheKey]: {
                  index,
                  hasMore: normalized.length >= PREFETCH_LIMIT,
                  lastAccessed: Date.now(),
                },
              }),
            }));
          } catch (error) {
            console.warn('Failed to prefetch emails', error);
          } finally {
            set((state) => {
              const next = new Map(state.inFlightRequests);
              next.delete(requestKey);
              return { inFlightRequests: next };
            });
          }
        })();

        set((state) => {
          const next = new Map(state.inFlightRequests);
          next.set(requestKey, { promise, requestId: 0 });
          return { inFlightRequests: next };
        });
        return promise;
      },

      prefetchEmail: async (emailId, accountId, folderId, threadId = null, messageCount = 1) => {
        if (!emailId || !accountId || accountId === UNIFIED_ACCOUNT_ID) {
          return;
        }
        const state = get();
        // Already hydrated (body cached) — nothing to warm.
        const identity = findIdentityById(state.emailReverseIndex, emailId);
        const cached = identity !== null ? state.emailIndex[identity] : null;
        if (cached && (cached.bodyHtml || cached.body)) {
          return;
        }
        const requestKey = `email-prefetch:${accountId}:${emailId}`;
        const existing = state.inFlightRequests.get(requestKey);
        if (existing) {
          return existing.promise;
        }

        const wantThread = Boolean(threadId) && (messageCount ?? 1) > 1;
        const promise = (async () => {
          try {
            const [serverEmail] = await Promise.all([
              fetchEmail(accountId, emailId, folderId ?? undefined),
              wantThread ? fetchThread(accountId, threadId as string).catch(() => null) : Promise.resolve(null),
            ]);
            if (!serverEmail) {
              return;
            }
            // Merge the fetched body into the index so a later open seeds an
            // optimistic header WITH body (P14) and the network hit is a cheap
            // 304 (P16). Never re-project — a body merge can't change the list
            // (P10), and prefetch must not disturb the visible order.
            set((s) => {
              const id = findIdentityById(s.emailReverseIndex, serverEmail.id);
              if (id === null) {
                return {};
              }
              return {
                emailIndex: {
                  ...s.emailIndex,
                  [id]: mergeEmailUpdate(
                    s.emailIndex[id],
                    applyOverlayToEmail(applyAttachmentState(serverEmail))
                  ),
                },
              };
            });
          } catch {
            // Best-effort warmup — a failed prefetch just means the open pays
            // full latency, exactly as before.
          } finally {
            set((s) => {
              const next = new Map(s.inFlightRequests);
              next.delete(requestKey);
              return { inFlightRequests: next };
            });
          }
        })();

        set((s) => {
          const next = new Map(s.inFlightRequests);
          next.set(requestKey, { promise, requestId: 0 });
          return { inFlightRequests: next };
        });
        return promise;
      },
    };
  }),
  // @ts-expect-error - Zustand middleware type inference issue with optional options parameter
  { name: 'EmailStore' }
));

let unsubFolderWatch: (() => void) | null = null;
let folderWatchDisposed = false;

const startFolderWatch = () => {
  if (folderWatchDisposed || unsubFolderWatch) {
    return;
  }
  unsubFolderWatch = useFolderStore.subscribe(
    (state) => ({
      folders: state.folders,
      accountFolderCache: state.accountFolderCache,
    }),
    () => {
      const emailState = useEmailStore.getState();
      const pending = emailState.pendingUnifiedRequest;
      if (pending) {
        // Don't load regular emails if we're in a sender group - the sender group has its own emails
        const senderGroupingState = useSenderGroupingStore.getState();
        if (senderGroupingState.activeSenderGroup) {
          useEmailStore.setState({ pendingUnifiedRequest: null });
          return;
        }

        const sources = resolveFolderSources(pending.accountId, pending.folderId);
        if (!sources.length) {
          return;
        }
        useEmailStore.setState({ pendingUnifiedRequest: null });
        void emailState.loadEmails(pending.accountId, pending.folderId, pending.options);
        return;
      }

      // Self-heal a unified list loaded from a PARTIAL source set. On a cold
      // start the unified folder aggregation is rebuilt as each account's
      // folder list arrives; a unified load issued in that window fetches only
      // the early accounts' mail — the header says "All accounts" while the
      // list shows one account — and nothing re-triggered it (the pending
      // mechanism above only covers the zero-source case). When the resolved
      // membership for the loaded unified folder changes, reload it.
      const lastUnified = emailState.lastUnifiedLoad;
      if (!lastUnified) {
        return;
      }
      if (useSenderGroupingStore.getState().activeSenderGroup) {
        return;
      }
      const sources = resolveFolderSources(lastUnified.accountId, lastUnified.folderId);
      if (!sources.length) {
        return;
      }
      if (unifiedSourcesKey(sources) === lastUnified.sourcesKey) {
        return;
      }
      void emailState.loadEmails(lastUnified.accountId, lastUnified.folderId, { reset: true });
    }
  );
};

if (typeof queueMicrotask === 'function') {
  queueMicrotask(startFolderWatch);
} else {
  setTimeout(startFolderWatch, 0);
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    folderWatchDisposed = true;
    unsubFolderWatch?.();
    unsubFolderWatch = null;
  });
}

export const useEmails = (): Email[] => useEmailStore((state) => state?.emails ?? []);
export const useEmailsLoading = () => useEmailStore((state) => state.emailsLoading);
export const useSelectedEmail = () => useEmailStore((state) => state.selectedEmail);
export const useSelectedEmailId = () => useEmailStore((state) => state.selectedEmailId);
export const useSelectedThread = () => useEmailStore((state) => state.selectedThread);
export const useEmailDetailLoading = () => useEmailStore((state) => state.emailDetailLoading);
